import { useState } from 'react';

import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import type { UserWithActivity } from '@/api/queries/analyticsQueries/useFetchOrgUsers';
import { useFetchOrgUsers } from '@/api/queries/analyticsQueries/useFetchOrgUsers';
import { useManagedUsers } from '@/api/queries/useManagedUsers';
import { AddUsersDialog } from '@/new/components/add-users-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/new/components/shadcn/empty';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { UsersTable } from '@/new/components/users-table';

export function ManagedUsersPanel({
  organisationId,
  onViewUserMatters,
  showBlockControls,
  isSeatBased = false,
  requiresExternalUser2fa = false,
  externalUserSettingsHref,
}: {
  organisationId: string;
  onViewUserMatters?: (user: UserWithActivity) => void;
  showBlockControls: boolean;
  isSeatBased?: boolean;
  requiresExternalUser2fa?: boolean;
  externalUserSettingsHref?: string;
}) {
  const { query, add } = useManagedUsers(organisationId);
  const usersQuery = useFetchOrgUsers(organisationId);
  const [adding, setAdding] = useState(false);

  if (query.isLoading) {
    return (
      <UsersTable
        organisationId={organisationId}
        onViewUserMatters={onViewUserMatters}
        isSeatBased={isSeatBased}
        showBlockControls={showBlockControls}
        loading
      />
    );
  }

  if (query.isError || !query.data) {
    return (
      <Empty className="py-16" role="alert">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon className="lucide--triangle-alert" />
          </EmptyMedia>
          <EmptyTitle>Could not load user management</EmptyTitle>
          <EmptyDescription>Something went wrong while loading the user management settings.</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button onClick={() => void query.refetch()}>Try again</Button>
        </EmptyContent>
      </Empty>
    );
  }

  const { isSso } = query.data;
  const externalUserIds = (usersQuery.data ?? []).filter((user) => user.isExternal).map((user) => user.id);
  const memberUserIds = (usersQuery.data ?? []).filter((user) => !user.isExternal).map((user) => user.id);
  const addButton = (
    <Button
      variant="primary"
      onClick={() => {
        add.reset();
        setAdding(true);
      }}
    >
      <Icon className="lucide--user-plus-2" />
      {isSso ? 'Add external users' : 'Add users'}
    </Button>
  );
  const tableProps = { organisationId, onViewUserMatters, isSeatBased };

  return (
    <>
      {isSso ? (
        <Tabs defaultValue="members">
          <div className="px-8">
            <TabsList>
              <TabsTrigger value="members">Members</TabsTrigger>
              <TabsTrigger value="external">External users ({externalUserIds.length})</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="members">
            <UsersTable {...tableProps} showBlockControls={showBlockControls} includeUserIds={memberUserIds} />
          </TabsContent>
          <TabsContent value="external">
            <div className="mt-4 mb-4 px-10 text-gray-500">
              <p className="max-w-3xl">
                External users sign in without your organisation’s SSO. Manage their Wexler roles
                {onViewUserMatters ? ', Matter access' : ''} and account access below. Two-factor authentication is{' '}
                {requiresExternalUser2fa ? 'required' : 'optional'} for non-SSO users.
                {externalUserSettingsHref && (
                  <>
                    {' '}
                    <Link className="text-blue-500 underline" to={externalUserSettingsHref}>
                      Manage in Organisation Settings
                    </Link>
                  </>
                )}
                .
              </p>
            </div>
            <UsersTable
              {...tableProps}
              showBlockControls
              includeUserIds={externalUserIds}
              accessActionTerminology="disable"
              additionalButtons={addButton}
              plural="external users"
            />
          </TabsContent>
        </Tabs>
      ) : (
        <UsersTable {...tableProps} showBlockControls={showBlockControls} additionalButtons={addButton} />
      )}

      <AddUsersDialog
        open={adding}
        onOpenChange={setAdding}
        maxRecipients={100}
        title={isSso ? 'Add external users to your organisation' : 'Add users to your organisation'}
        description={
          <p>
            {isSso
              ? 'These users will sign in without SSO and receive your organisation’s default Wexler role.'
              : 'Users receive your organisation’s default Wexler role.'}{' '}
            New accounts receive a confirmation email.
          </p>
        }
        submitButtonLabel={(count) => `Add ${count} ${count === 1 ? 'user' : 'users'}`}
        submitErrorMessage={(error) =>
          error instanceof Error ? error.message : 'Users could not be added. Please try again.'
        }
        getRecipientPresentation={(email) => {
          const result = add.data?.results.find((candidate) => candidate.email === email);
          if (result?.status === 'failed') {
            return { badge: <span className="text-sm text-red-700">{result.message}</span> };
          }
          if (result?.status === 'pendingSync') {
            return { badge: <span className="text-sm text-amber-700">{result.message}</span> };
          }
          return { badge: undefined };
        }}
        onSubmit={async (emails) => {
          const response = await add.mutateAsync(emails);
          const failedResults = response.results.filter(({ status }) => status === 'failed');
          const pendingResults = response.results.filter(({ status }) => status === 'pendingSync');
          const addedResults = response.results.filter(({ status }) => status === 'added');
          if (addedResults.length > 0) {
            toast.success(`${addedResults.length} ${addedResults.length === 1 ? 'user was' : 'users were'} added.`);
          }
          if (pendingResults.length > 0) {
            toast.warning(
              `${pendingResults.length} ${pendingResults.length === 1 ? 'user is' : 'users are'} still being set up. They will appear here once Wexler finishes syncing the account.`,
            );
          }
          if (failedResults.length > 0) {
            toast.error(
              `${failedResults.length} ${failedResults.length === 1 ? 'user could' : 'users could'} not be added. Please try again.`,
            );
          }
          return { failedEmails: [...failedResults, ...pendingResults].map(({ email }) => email) };
        }}
      />
    </>
  );
}
